import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Modal, Select, Tooltip } from 'antd'
import { Button, Chevron, theme } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import useGenerate from 'screens/Rostering/hooks/useGenerate'
import DateRange from '../DateRange'
import { DAY_FIRST_DATE_FORMAT } from 'screens/Rostering/utils.datetime'
import { QuestionCircleOutlined } from '@ant-design/icons'
import useAllocationTemplateList from 'screens/Rostering/hooks/useAllocationTemplateList'
import { useState } from 'react'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import dayjs from 'dayjs'

type PublishModalProps = {
  open: boolean
  onClose: (event?: any) => any
  fromDate: string
  toDate: string
}

const StartRotaPublishModal = ({
  open,
  onClose,
  fromDate,
  toDate,
}: PublishModalProps) => {
  const { t } = useTranslation()
  const { generateRota } = useGenerate({ startDate: fromDate })
  const sendEvent = useMixpanelEvent()

  const flags = useFeatureFlags({
    flagNames: ['rostering_allocation_template'],
  })?.data

  const flagsRosteringAllocationTemplate =
    flags?.rostering_allocation_template?.enabled

  const [templateValue, setTemplateValue] = useState<string | undefined>(
    undefined,
  )

  const { templateRotas } = useAllocationTemplateList()

  const templateAllocationMap = templateRotas.reduce(
    (map: Map<string, string>, templateRota: any) => {
      // eslint-disable-next-line no-underscore-dangle
      map.set(templateRota.templateAlias, templateRota._id)
      return map
    },
    new Map(),
  )

  const transformedTemplateOptions = Array.from(
    templateAllocationMap,
    ([templateAlias]) => ({
      label: templateAlias,
      value: templateAlias,
    }),
  )

  const onGenerate = () => {
    generateRota({
      allocationTemplateName: templateValue,
      allocationTemplateId: templateAllocationMap.get(templateValue),
    })

    sendEvent(MixpanelEvents.START_ROTA_GENERATED, {
      clickedAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      rotaStartDate: fromDate,
      rotaEndDate: toDate,
      templateUsed: !!templateValue,
      templateName: templateValue || null,
      templateId: templateValue
        ? templateAllocationMap.get(templateValue)
        : null,
    })

    onClose()
  }

  return (
    <Modal
      data-cy="container-start-rota-modal"
      destroyOnClose
      width={470}
      title={<Title level={1}>{t('START_DRAFT_ROTA')}</Title>}
      footer={
        <ModalFooter data-cy="container-footer-start-rota-modal">
          <Button onClick={onClose}>{t('CANCEL')}</Button>
          <Button
            onClick={onGenerate}
            variant="primary"
            data-cy="btn-start-rota-modal"
          >
            {t('START_ROTA')}
          </Button>
        </ModalFooter>
      }
      open={open}
      onCancel={onClose}
    >
      <ModalDivider />
      <DateRange
        fromDate={fromDate}
        toDate={toDate}
        dateFormat={DAY_FIRST_DATE_FORMAT}
      />
      <Text>{t('GENERATE_VISITS_AND_SHIFTS')}</Text>
      <ModalDivider />

      {flagsRosteringAllocationTemplate &&
        transformedTemplateOptions.length > 0 && (
          <AllocationTemplateSection>
            <StyledLabel>
              <Text>{t('SELECT_ALLOCATION_TEMPLATE')}</Text>
              <Tooltip
                color={theme.color.neutral.color3}
                placement="bottom"
                title={
                  <TooltipContent>
                    {t('SELECT_ALLOCATION_TEMPLATE_TOOLTIP')}
                  </TooltipContent>
                }
                overlayInnerStyle={{ width: '400px', padding: '8px 16px' }}
              >
                <MoreInfoIcon />
              </Tooltip>
              <LightText>{t('OPTIONAL')}</LightText>
            </StyledLabel>
            <Select
              placeholder={<Text>{t('PLEASE_SELECT')}</Text>}
              data-cy="dropdown-rota-template-picker"
              suffixIcon={<Chevron direction="down" />}
              value={templateValue}
              onChange={(value) => setTemplateValue(value)}
              allowClear
            >
              {transformedTemplateOptions.map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  data-cy={`dropdown-rota-template-item-${option.value.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Text>{option.label}</Text>
                </Select.Option>
              ))}
            </Select>
            <ModalDivider />
          </AllocationTemplateSection>
        )}
    </Modal>
  )
}

export default StartRotaPublishModal

const Title = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  text-transform: none;
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
`

const ModalDivider = styled(Divider)`
  margin: 24px 0px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
`
const StyledLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const AllocationTemplateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const TooltipContent = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`
const MoreInfoIcon = styled(QuestionCircleOutlined)`
  font-size: 16px;
  margin-left: 4px;

  &:hover {
    color: ${(props) => props.theme.color.status.primary};
  }
`
const LightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
